import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import Select from '@shadcn/components/ui/react-select';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

const regionOptions = [
  { value: 'AU', label: 'AU' },
  { value: 'US', label: 'US' },
  { value: 'UK', label: 'UK' },
];

const viewTypeOptions = [
  { value: 'category', label: 'Category' },
  { value: 'product', label: 'Product' },
  { value: 'sku', label: 'SKU' }
];

const FilterSection = ({ 
  selectedRegion,
  setSelectedRegion,
  viewType,
  setViewType,
  selectedProductIds,
  selectedCategoryNames,
  onFiltersChange,
  loading: parentLoading,
  onOpenGrowthRateModal 
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Local state for UI - only applied on "Apply Filters"
  const [tempRegion, setTempRegion] = useState(selectedRegion);
  const [tempViewType, setTempViewType] = useState(viewType);
  const [tempProductIds, setTempProductIds] = useState(selectedProductIds || []);
  const [tempCategoryNames, setTempCategoryNames] = useState(selectedCategoryNames || []);

  // Fetch products/SKUs data
  const fetchActiveSkus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await superagent
        .get(`${backendUrl}/product/active-skus`)
        .query({ region: tempRegion })
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      if (response.body && Array.isArray(response.body)) {
        const productMap = new Map();
        
        response.body.forEach(item => {
          const key = `${item.productName}_${item.category}`;
          if (!productMap.has(key)) {
            productMap.set(key, {
              productId: item._id, // Use productName as productId
              productName: item.productName,
              category: item.category,
              sku: item.sku
            });
          }
        });
        
        setProducts(Array.from(productMap.values()));
      }
    } catch (err) {
      setError('Failed to fetch products data');
      console.error('Failed to fetch active SKUs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tempRegion) {
      fetchActiveSkus();
    }
  }, [tempRegion]);

  // Update temp state when props change
  useEffect(() => {
    setTempRegion(selectedRegion);
    setTempViewType(viewType);
    setTempProductIds(selectedProductIds || []);
    setTempCategoryNames(selectedCategoryNames || []);
  }, [selectedRegion, viewType, selectedProductIds, selectedCategoryNames]);

  // Generate dropdown options with 2-way relationship
  const productOptions = useMemo(() => {
    let filteredProducts = products;
    
    // Filter products by selected categories (Category → Product relationship)
    if (tempCategoryNames.length > 0) {
      filteredProducts = products.filter(product => 
        tempCategoryNames.includes(product.category)
      );
    }
    
    console.log('filteredProducts', filteredProducts);
    return filteredProducts.map(product => ({
      value: product.productId,
      label: product.productName,
      category: product.category
    }));
  }, [products, tempCategoryNames]);

  const categoryOptions = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category))].filter(Boolean);
    return uniqueCategories.sort().map(category => ({
      value: category,
      label: category
    }));
  }, [products]);

  // Handle product selection (Product → Category relationship)
  const handleProductChange = (selectedProducts) => {
    console.log('selectedProducts', selectedProducts);
    const productIds = selectedProducts ? selectedProducts.map(s => s.value) : [];
    setTempProductIds(productIds);
    
    // Auto-select categories for selected products
    if (selectedProducts && selectedProducts.length > 0) {
      const selectedCategories = [...new Set(selectedProducts.map(p => p.category))];
      const currentCategories = new Set(tempCategoryNames);
      
      // Add missing categories
      selectedCategories.forEach(category => currentCategories.add(category));
      setTempCategoryNames(Array.from(currentCategories));
    }
  };

  // Handle category selection (Category → Product relationship)
  const handleCategoryChange = (selectedCategories) => {
    const categoryNames = selectedCategories ? selectedCategories.map(s => s.value) : [];
    setTempCategoryNames(categoryNames);
    
    // Filter out products that don't belong to selected categories
    if (categoryNames.length > 0) {
      const validProductIds = tempProductIds.filter(productId => {
        const product = products.find(p => p.productId === productId);
        return product && categoryNames.includes(product.category);
      });
      
      if (validProductIds.length !== tempProductIds.length) {
        setTempProductIds(validProductIds);
      }
    }
  };

  const handleApplyFilters = () => {
    // Update region and viewType
    setSelectedRegion(tempRegion);
    setViewType(tempViewType);
    
    // Update backend filters
    onFiltersChange({
      productIds: tempProductIds,
      categoryNames: tempCategoryNames
    });
    
    // Auto-scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setTempProductIds([]);
    setTempCategoryNames([]);
    onFiltersChange({
      productNames: [],
      productIds: [],
      categoryNames: []
    });
  };

  const hasChanges = useMemo(() => {
    const currentProductIds = selectedProductIds || [];
    const currentCategoryNames = selectedCategoryNames || [];
    
    return (
      tempRegion !== selectedRegion ||
      tempViewType !== viewType ||
      JSON.stringify(tempProductIds.sort()) !== JSON.stringify(currentProductIds.sort()) ||
      JSON.stringify(tempCategoryNames.sort()) !== JSON.stringify(currentCategoryNames.sort())
    );
  }, [tempRegion, tempViewType, tempProductIds, tempCategoryNames, selectedRegion, viewType, selectedProductIds, selectedCategoryNames]);

  const activeFiltersCount = useMemo(() => {
    return (selectedProductIds?.length || 0) + (selectedCategoryNames?.length || 0);
  }, [selectedProductIds, selectedCategoryNames]);

  const hasActiveFilters = activeFiltersCount > 0;

  if (loading) {
    return (
      <Card className="mb-6 rounded-2xl shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-center h-20">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <span className="text-sm text-gray-500">Loading filter options...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 rounded-2xl shadow-sm border">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {activeFiltersCount} active
                </span>
              )}
              <Button
                onClick={onOpenGrowthRateModal}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Set Growth Rate
              </Button>
              {hasActiveFilters && (
                <Button 
                  onClick={handleClearFilters}
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                >
                  Clear All
                </Button>
              )}
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Region Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Region
              </Label>
              <Select
                options={regionOptions}
                value={regionOptions.find(opt => opt.value === tempRegion)}
                onChange={(selected) => setTempRegion(selected?.value || 'AU')}
                placeholder="Select region..."
                className="text-sm"
                classNamePrefix="react-select"
                isDisabled={parentLoading}
              />
            </div>

            {/* View Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                View By
              </Label>
              <Select
                options={viewTypeOptions}
                value={viewTypeOptions.find(opt => opt.value === tempViewType)}
                onChange={(selected) => setTempViewType(selected?.value || 'category')}
                placeholder="Select view type..."
                className="text-sm"
                classNamePrefix="react-select"
                isDisabled={parentLoading}
              />
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Categories
              </Label>
              <Select
                isMulti
                options={categoryOptions}
                value={categoryOptions.filter(opt => tempCategoryNames.includes(opt.value))}
                onChange={handleCategoryChange}
                placeholder="Select categories..."
                className="text-sm"
                classNamePrefix="react-select"
                isDisabled={parentLoading}
                maxMenuHeight={200}
              />
              {tempCategoryNames.length > 0 && (
                <p className="text-xs text-gray-500">
                  {tempCategoryNames.length} categor{tempCategoryNames.length !== 1 ? 'ies' : 'y'} selected
                </p>
              )}
            </div>

            {/* Product Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Products
              </Label>
              <Select
                isMulti
                options={productOptions}
                value={productOptions.filter(opt => tempProductIds.includes(opt.value))}
                onChange={handleProductChange}
                placeholder="Select products..."
                className="text-sm"
                classNamePrefix="react-select"
                isDisabled={parentLoading}
                maxMenuHeight={200}
              />
              {tempProductIds.length > 0 && (
                <p className="text-xs text-gray-500">
                  {tempProductIds.length} product{tempProductIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>

          {/* Apply Button */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleApplyFilters}
              disabled={!hasChanges || parentLoading}
              className="min-w-24"
            >
              {parentLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>Applying...</span>
                </div>
              ) : (
                'Apply Filters'
              )}
            </Button>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="border-t pt-3 animate-fade-in">
              <div className="text-xs text-gray-600 mb-2">Active filters:</div>
              <div className="flex flex-wrap gap-2">
                {selectedProductIds?.map(productId => {
                  const product = productOptions.find(p => p.value === productId);
                  return product ? (
                    <span
                      key={productId}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                    >
                      📦 {product.label}
                    </span>
                  ) : null;
                })}
                {selectedCategoryNames?.map(categoryName => (
                  <span
                    key={categoryName}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                  >
                    📂 {categoryName}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterSection; 
